/**
 * Samsung SSSP (URL Launcher) implementation of PlatformApi.
 *
 * Used when the player runs as a URL Launcher app on Samsung commercial
 * displays (QBC, SSSP 10, Tizen 7). The container is a fullscreen
 * Chromium browser with Samsung's b2bapis / webapis injected, but NO
 * `window.tizen` — so we can't use the full Tizen Web Device API.
 *
 * Strategy:
 *   - storage:    Cache Storage (same as web.ts) — webapis.filesystem
 *                 varies by firmware and is not reliable enough as the
 *                 primary store. Cache Storage persists across reboots
 *                 in URL Launcher mode and is the recommended path.
 *   - systemInfo: webapis.systeminfo + webapis.productinfo for real
 *                 CPU/RAM/firmware/model, fallback to navigator.* if a
 *                 specific call isn't available on this firmware.
 *   - power:      webapis.power.request('SCREEN', 'SCREEN_NORMAL') —
 *                 same API surface as on full Tizen.
 *   - network:    webapis.network.isConnectedToGateway() +
 *                 getActiveConnectionType().
 *   - application: stub — no app exit/info in URL Launcher (it's a
 *                  browser tab in disguise). exit() reloads the page,
 *                  which is what the kiosk wants anyway.
 *   - alarm:      setTimeout fallback (no tizen.alarm here).
 *
 * Detection (in platform/index.ts) is `window.b2bapis || window.webapis`
 * with `window.tizen` taking priority — so a full .wgt context never
 * lands here.
 */

import type {
  AlarmHandle,
  NetworkInfo,
  NetworkType,
  PlatformApi,
  PlatformAlarm,
  PlatformApplication,
  PlatformNetwork,
  PlatformPower,
  PlatformStorage,
  PlatformSystemInfo,
  PowerLock,
  StorageEntry,
  SystemInfo,
} from './types';

const APP_START = Date.now();

// ── Storage (Cache Storage API — same approach as web.ts) ───────────

const BYTES_CACHE = 'sensar-storage-v1';
const META_CACHE = 'sensar-storage-meta-v1';

interface PersistedMeta {
  key: string;
  size: number;
  contentType: string | null;
  storedAt: number;
}

function storageKey(key: string): string {
  return `/${encodeURIComponent(key)}`;
}

class SsspStorage implements PlatformStorage {
  readonly persistent = true;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = (async () => {
      if (typeof caches === 'undefined') {
        console.warn('[platform/sssp] Cache Storage unavailable');
        return;
      }
      await Promise.all([caches.open(BYTES_CACHE), caches.open(META_CACHE)]);
      if (navigator.storage?.persist) {
        try {
          const granted = await navigator.storage.persist();
          console.log(`[platform/sssp] persistent storage granted: ${granted}`);
        } catch {
          // No user gesture / not supported on this firmware — ignore.
        }
      }
    })();
    return this.initPromise;
  }

  async get(key: string): Promise<Blob | null> {
    if (typeof caches === 'undefined') return null;
    const cache = await caches.open(BYTES_CACHE);
    const res = await cache.match(storageKey(key));
    return res ? await res.blob() : null;
  }

  async put(key: string, body: Blob, contentType?: string): Promise<StorageEntry> {
    if (typeof caches === 'undefined') {
      throw new Error('Cache Storage unavailable on this firmware');
    }
    const ct = contentType ?? body.type ?? 'application/octet-stream';
    const meta: PersistedMeta = {
      key,
      size: body.size,
      contentType: ct,
      storedAt: Date.now(),
    };
    const metaCache = await caches.open(META_CACHE);
    await metaCache.put(
      storageKey(key),
      new Response(JSON.stringify(meta), {
        headers: { 'Content-Type': 'application/json' },
      }),
    );
    const bytesCache = await caches.open(BYTES_CACHE);
    await bytesCache.put(
      storageKey(key),
      new Response(body, { headers: { 'Content-Type': ct } }),
    );
    return { key, size: meta.size, contentType: meta.contentType, storedAt: meta.storedAt };
  }

  async remove(key: string): Promise<void> {
    if (typeof caches === 'undefined') return;
    const [bytesCache, metaCache] = await Promise.all([
      caches.open(BYTES_CACHE),
      caches.open(META_CACHE),
    ]);
    await Promise.all([
      bytesCache.delete(storageKey(key)),
      metaCache.delete(storageKey(key)),
    ]);
  }

  async list(): Promise<StorageEntry[]> {
    if (typeof caches === 'undefined') return [];
    const metaCache = await caches.open(META_CACHE);
    const keys = await metaCache.keys();
    const out: StorageEntry[] = [];
    for (const req of keys) {
      const res = await metaCache.match(req);
      if (!res) continue;
      try {
        const m = (await res.json()) as PersistedMeta;
        out.push({
          key: m.key,
          size: m.size,
          contentType: m.contentType,
          storedAt: m.storedAt,
        });
      } catch {
        // Skip corrupt entries.
      }
    }
    return out;
  }

  async getQuota(): Promise<{ usage: number; quota: number }> {
    if (!navigator.storage?.estimate) return { usage: -1, quota: -1 };
    try {
      const est = await navigator.storage.estimate();
      return { usage: est.usage ?? -1, quota: est.quota ?? -1 };
    } catch {
      return { usage: -1, quota: -1 };
    }
  }
}

// ── System info (webapis.systeminfo + productinfo) ──────────────────

function getCpuLoad(): Promise<number> {
  const si = window.webapis?.systeminfo;
  if (!si) return Promise.resolve(-1);
  if (typeof si.getCpuUsageSync === 'function') {
    try {
      const v = si.getCpuUsageSync();
      // Some firmwares return 0..100, some 0..1 — normalise to 0..1.
      return Promise.resolve(v > 1 ? v / 100 : v);
    } catch {
      return Promise.resolve(-1);
    }
  }
  if (typeof si.getCpuUsage === 'function') {
    return new Promise((resolve) => {
      try {
        si.getCpuUsage!(
          (v: number) => resolve(v > 1 ? v / 100 : v),
          () => resolve(-1),
        );
      } catch {
        resolve(-1);
      }
    });
  }
  return Promise.resolve(-1);
}

class SsspSystemInfoImpl implements PlatformSystemInfo {
  readonly available = true;

  async collect(): Promise<SystemInfo> {
    const si = window.webapis?.systeminfo;
    const pi = window.webapis?.productinfo;

    // CPU
    const cpuLoad = await getCpuLoad();

    // Memory: SSSP exposes total/available in bytes.
    let ramUsedBytes = -1;
    if (si?.getTotalMemory && si?.getAvailableMemory) {
      try {
        const total = si.getTotalMemory();
        const avail = si.getAvailableMemory();
        if (typeof total === 'number' && typeof avail === 'number' && total > 0) {
          ramUsedBytes = Math.max(0, total - avail);
        }
      } catch {
        // ignore
      }
    }
    // Fallback to non-standard performance.memory if SSSP didn't answer.
    if (ramUsedBytes < 0) {
      const perfMem = (performance as { memory?: { usedJSHeapSize?: number } }).memory;
      if (perfMem?.usedJSHeapSize) ramUsedBytes = perfMem.usedJSHeapSize;
    }

    // Disk: Cache Storage estimate is the only reliable cross-firmware
    // path. Real disk capacity would require webapis.filesystem which
    // varies between models.
    let diskFreeBytes = -1;
    let diskUsedPercent = -1;
    if (navigator.storage?.estimate) {
      try {
        const est = await navigator.storage.estimate();
        if (est.usage != null && est.quota != null && est.quota > 0) {
          diskUsedPercent = Math.round((est.usage / est.quota) * 100);
          diskFreeBytes = est.quota - est.usage;
        }
      } catch {
        // ignore
      }
    }

    let firmwareVersion = '';
    let deviceModel = '';
    try {
      if (pi?.getFirmware) firmwareVersion = pi.getFirmware();
      if (!firmwareVersion && pi?.getSmartSignageVersion) {
        firmwareVersion = `SSSP ${pi.getSmartSignageVersion()}`;
      }
    } catch {
      // ignore
    }
    try {
      if (pi?.getModel) deviceModel = pi.getModel();
    } catch {
      // ignore
    }
    if (!firmwareVersion) firmwareVersion = navigator.userAgent;
    if (!deviceModel) deviceModel = navigator.platform || '';

    const appUptimeHours = (Date.now() - APP_START) / 3_600_000;
    const displayResolution = window.screen
      ? { width: window.screen.width, height: window.screen.height }
      : null;

    return {
      diskUsedPercent,
      diskFreeBytes,
      ramUsedBytes,
      cpuLoad,
      appUptimeHours,
      firmwareVersion,
      deviceModel,
      displayResolution,
    };
  }
}

// ── Power (webapis.power) ───────────────────────────────────────────

class SsspPowerImpl implements PlatformPower {
  readonly available = !!window.webapis?.power;

  async keepScreenOn(): Promise<PowerLock> {
    const p = window.webapis?.power;
    if (!p?.request) {
      // No SSSP power API — fall back to Wake Lock if the browser has it.
      const nav = navigator as Navigator & {
        wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
      };
      if (nav.wakeLock) {
        try {
          const sentinel = await nav.wakeLock.request('screen');
          return {
            release: () => {
              void sentinel.release();
            },
          };
        } catch {
          return { release: () => {} };
        }
      }
      return { release: () => {} };
    }
    try {
      p.request('SCREEN', 'SCREEN_NORMAL');
    } catch (err) {
      console.warn('[platform/sssp] power.request failed', err);
    }
    return {
      release: () => {
        try {
          p.release?.('SCREEN');
        } catch {
          // ignore
        }
      },
    };
  }
}

// ── Network (webapis.network) ───────────────────────────────────────

function ssspConnectionType(): NetworkType {
  const n = window.webapis?.network;
  if (!n?.getActiveConnectionType) {
    return navigator.onLine ? 'unknown' : 'offline';
  }
  try {
    const t = String(n.getActiveConnectionType() || '').toUpperCase();
    if (t.includes('WIFI')) return 'wifi';
    if (t.includes('WIRED') || t.includes('ETHERNET')) return 'ethernet';
    if (t.includes('CELLULAR') || t.includes('LTE')) return 'cellular';
    if (t.includes('DISCONNECTED') || t === '') return 'offline';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

function ssspIsOnline(): boolean {
  const n = window.webapis?.network;
  if (n?.isConnectedToGateway) {
    try {
      const v = n.isConnectedToGateway();
      return v === 1 || v === true;
    } catch {
      // fall through
    }
  }
  return navigator.onLine;
}

class SsspNetworkImpl implements PlatformNetwork {
  readonly available = true;

  async info(): Promise<NetworkInfo> {
    const n = window.webapis?.network;
    let ipAddress: string | undefined;
    let macAddress: string | undefined;
    try {
      if (n?.getIp) ipAddress = n.getIp() || undefined;
    } catch {
      // ignore
    }
    try {
      if (n?.getMac) macAddress = n.getMac() || undefined;
    } catch {
      // ignore
    }
    return {
      type: ssspConnectionType(),
      online: ssspIsOnline(),
      ipAddress,
      macAddress,
    };
  }

  onChange(cb: (info: NetworkInfo) => void): () => void {
    // SSSP doesn't expose a native subscription — fall back to browser
    // online/offline events, which fire correctly under URL Launcher.
    const emit = () => {
      void this.info().then(cb);
    };
    window.addEventListener('online', emit);
    window.addEventListener('offline', emit);
    return () => {
      window.removeEventListener('online', emit);
      window.removeEventListener('offline', emit);
    };
  }
}

// ── Application ─────────────────────────────────────────────────────

class SsspApplicationImpl implements PlatformApplication {
  readonly available = true;

  async info() {
    const pi = window.webapis?.productinfo;
    let version = '';
    try {
      if (pi?.getSmartSignageVersion) version = pi.getSmartSignageVersion();
      else if (pi?.getVersion) version = pi.getVersion();
    } catch {
      // ignore
    }
    return {
      appId: 'sssp.url-launcher',
      version: version || 'unknown',
      packageId: 'sssp',
    };
  }

  exit(): void {
    // No way to actually kill a URL Launcher session — reload restarts
    // the player, which is what the kiosk wants anyway.
    window.location.reload();
  }
}

// ── Alarm (setTimeout) ──────────────────────────────────────────────

class SsspAlarmImpl implements PlatformAlarm {
  readonly available = true;

  async scheduleRelative(
    delaySec: number,
    callback: () => void,
  ): Promise<AlarmHandle> {
    const id = `sssp-alarm-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timer = setTimeout(callback, delaySec * 1000);
    return {
      id,
      cancel: () => clearTimeout(timer),
    };
  }
}

// ── Public api ──────────────────────────────────────────────────────

export const ssspPlatform: PlatformApi = {
  kind: 'sssp',
  storage: new SsspStorage(),
  systemInfo: new SsspSystemInfoImpl(),
  power: new SsspPowerImpl(),
  network: new SsspNetworkImpl(),
  application: new SsspApplicationImpl(),
  alarm: new SsspAlarmImpl(),
};
